import { AudioOutlined, LinkOutlined, SendOutlined } from '@ant-design/icons';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { useCopilot } from '../../CopilotContext';

import styles from './Composer.module.css';

/**
 * Text + voice composer. The mic streams audio to the copilot's server-side
 * STT; the final transcript is injected here for the doctor to review and Send
 * (no auto-send). The attach button is design-only for now.
 */
export function Composer() {
  const {
    sendText,
    status,
    isRecording,
    setRecording,
    pendingTranscript,
    clearPendingTranscript,
  } = useCopilot();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const resetHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const grow = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  // A finalized dictation transcript drops into the composer for review.
  useEffect(() => {
    if (!pendingTranscript) return;
    setText((prev) =>
      prev ? `${prev} ${pendingTranscript}` : pendingTranscript
    );
    clearPendingTranscript();
    requestAnimationFrame(() => {
      grow();
      textareaRef.current?.focus();
    });
  }, [pendingTranscript, clearPendingTranscript]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    sendText(t);
    setText('');
    resetHeight();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canConnect = status === 'open';

  return (
    <div className={styles.wrap}>
      <button
        className={styles.iconBtn}
        type="button"
        aria-label="Attach"
        disabled
      >
        <LinkOutlined />
      </button>
      <textarea
        ref={textareaRef}
        className={styles.input}
        placeholder={isRecording ? 'Listening…' : 'Ask a question'}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          grow();
        }}
        onKeyDown={onKeyDown}
        rows={1}
        disabled={!canConnect}
      />
      <button
        className={`${styles.iconBtn} ${styles.micBtn} ${
          isRecording ? styles.micActive : ''
        }`}
        type="button"
        aria-label={isRecording ? 'Stop dictation' : 'Start voice dictation'}
        aria-pressed={isRecording}
        title={isRecording ? 'Stop dictation' : 'Voice dictation'}
        onClick={() => setRecording(!isRecording)}
        disabled={!canConnect}
      >
        <AudioOutlined />
      </button>
      <button
        className={`${styles.iconBtn} ${styles.sendBtn}`}
        type="button"
        aria-label="Send"
        onClick={submit}
        disabled={!text.trim() || !canConnect}
      >
        <SendOutlined />
      </button>
    </div>
  );
}
